import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { ReactComponent as RadioOn } from '../../../assets/images/svg/radio-on.svg';
import { ReactComponent as RadioOff } from '../../../assets/images/svg/radio-off.svg';
import { variables } from '../../../assets/styles/variables';
import themeColors from '../../../assets/styles/themeColors';
import {
  uButtonClear,
  uFullWidth,
  uOutlineNone,
} from '../../../assets/styles/utility';
import { mediaBelow, pxToRem, pxToRemMd } from '../../../assets/styles/helper';

const SelectItemElement = styled.li`
  display: flex;
  flex-direction: column;
  border: 1px solid transparent;
  border-radius: ${variables.borderRadius.borderRadius};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
  background-color: ${themeColors.colorBackgroundSecondary};
  position: relative;

  ${(props) =>
    props.isSelected && `border-color: ${themeColors.colorTextSecondary}`};
`;

const SelectItemButton = styled.button`
  ${uButtonClear};
  ${uOutlineNone};
  display: flex;
  align-items: center;
  cursor: pointer;
  ${uFullWidth};
  padding: ${pxToRem(10)} ${pxToRem(12)};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
`;

const SelectItemIndicator = styled.span`
  width: 16px;
  margin-right: ${pxToRem(10)};
`;

const SelectItemRibbon = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  background-color: ${({ backgroundColor }) =>
    backgroundColor || themeColors.colorTextSecondary};
  color: ${({ color }) => color || themeColors.colorWhite};
  display: inline-flex;
  padding: 2px 6px;
  justify-content: center;
  align-items: center;
  font-size: ${pxToRem(14)};
  font-weight: 500;
  border-radius: 0 3px 0 2px;
`;

const SelectItemValue = styled.div`
  display: flex;
  align-items: center;
  font-size: ${pxToRem(14)};
  line-height: 1.75;
  text-align: left;
  color: ${themeColors.colorTextPrimary};
  font-weight: 500;
  width: 100%;

  span {
    font-size: ${pxToRem(12)};
    font-weight: 400;
    color: ${themeColors.colorTextSecondary};
    display: block;
  }
`;

const SelectItemIcon = styled.div`
  width: ${pxToRem(32)};
  height: ${pxToRem(32)};
  background-color: ${themeColors.colorGrey};
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 6px;
  border-radius: ${variables.borderRadius.borderRadius};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    width: ${pxToRemMd(28)};
    height: ${pxToRemMd(28)};
  }
`;

const SelectItemContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const SelectItemSubtitleWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SelectItemTitle = styled.p`
  font-size: 14px;
  color: ${themeColors.colorTextPrimary};
  font-weight: 500;
`;

const SelectItem = ({
  isSelected,
  selectItemRibbon,
  isDisabled,
  onClick,
  icon,
  selectItemTitle,
  selectItemSubtitle,
  backgroundColor,
  selectItemDescription,
  ...restProps
}) => (
  <SelectItemElement
    isSelected={isSelected}
    disabled={isDisabled}
    {...restProps}
  >
    {selectItemRibbon && (
      <SelectItemRibbon backgroundColor={backgroundColor}>
        {selectItemRibbon}
      </SelectItemRibbon>
    )}

    <SelectItemButton type="button" onClick={onClick} disabled={isDisabled}>
      <SelectItemIndicator>
        {isSelected ? <RadioOn /> : <RadioOff />}
      </SelectItemIndicator>
      <SelectItemValue>
        {icon && <SelectItemIcon>{icon}</SelectItemIcon>}
        <SelectItemContent>
          {selectItemTitle && (
            <SelectItemTitle>{selectItemTitle}</SelectItemTitle>
          )}
          {selectItemSubtitle && (
            <SelectItemSubtitleWrap>
              {selectItemSubtitle}
            </SelectItemSubtitleWrap>
          )}
          {selectItemDescription}
        </SelectItemContent>
      </SelectItemValue>
    </SelectItemButton>
  </SelectItemElement>
);

SelectItem.propTypes = {
  isSelected: PropTypes.bool,
  selectItemRibbon: PropTypes.string,
  onClick: PropTypes.func,
  isDisabled: PropTypes.bool,
  icon: PropTypes.node,
  selectItemTitle: PropTypes.string,
  selectItemSubtitle: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  backgroundColor: PropTypes.string,
  selectItemDescription: PropTypes.node,
};

export default SelectItem;
